<template>
  <div class="grain-monitoring-page">
    <div class="page-top">
      <div>
        <h2 class="page-title">车辆装粮监控</h2>
        <p class="page-desc">实时查看各仓房发粮口监控画面，保障装粮作业可视可控</p>
      </div>
      <div class="warehouse-select">
        <span class="select-label">选择仓房</span>
        <el-select
          v-model="selectedWarehouseId"
          placeholder="请选择仓房"
          filterable
          style="width: 280px"
          @change="onWarehouseChange"
        >
          <el-option
            v-for="item in warehouses"
            :key="item.id"
            :label="`${item.warehouseName || item.name}（${item.warehouseNo || item.code || '-'}）`"
            :value="item.id"
          />
        </el-select>
        <el-button type="primary" icon="el-icon-refresh" :loading="loading" @click="loadCameras">
          刷新画面
        </el-button>
      </div>
    </div>

    <div v-if="!selectedWarehouseId" class="empty-panel">
      <i class="el-icon-video-camera"></i>
      <p>请先选择仓房查看监控</p>
    </div>

    <div v-else-if="loading" class="empty-panel">
      <i class="el-icon-loading"></i>
      <p>加载监控画面中...</p>
    </div>

    <div v-else-if="cameras.length === 0" class="empty-panel">
      <i class="el-icon-warning-outline"></i>
      <p>该仓房暂无监控设备</p>
      <p class="sub">请先在「发粮口监控设备配置」中完成摄像头配置</p>
    </div>

    <div v-else class="camera-grid">
      <div v-for="(camera, index) in cameras" :key="camera.id || index" class="camera-card">
        <div class="camera-card-head">
          <div class="camera-title">
            <span class="status-dot online"></span>
            <span>{{ camera.name || `监控 ${index + 1}` }}</span>
          </div>
          <span class="camera-ip">{{ camera.ipAddress }}:{{ camera.port }}</span>
        </div>
        <div class="camera-feed">
          <div class="feed-placeholder">
            <i class="el-icon-video-camera"></i>
            <span>监控信号接入中</span>
          </div>
        </div>
        <div class="camera-card-foot">
          <span>发粮口 ID：{{ camera.outletId || '-' }}</span>
          <el-button type="text" size="small" @click="loadCameras">刷新</el-button>
        </div>
      </div>
    </div>

    <div v-if="selectedWarehouseName" class="status-bar">
      <i class="el-icon-success"></i>
      <span>当前仓房：{{ selectedWarehouseName }} · 监控链路已连接 · 共 {{ cameras.length }} 路画面</span>
    </div>
  </div>
</template>

<script>
import { warehouseApi, grainOutletDeviceApi } from '../utils/api'

export default {
  name: 'VehicleGrainMonitoring',
  data() {
    return {
      warehouses: [],
      selectedWarehouseId: null,
      selectedWarehouseName: '',
      cameras: [],
      loading: false
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    async init() {
      await this.loadWarehouses()
    },

    async loadWarehouses() {
      try {
        const res = await warehouseApi.getWarehouses()
        this.warehouses = res.data || []
        if (this.warehouses.length > 0 && !this.selectedWarehouseId) {
          const first = this.warehouses[0]
          this.selectedWarehouseId = first.id
          this.selectedWarehouseName = first.warehouseName || first.name
          await this.loadCameras()
        }
      } catch (e) {
        this.$message.error('加载仓房列表失败')
      }
    },

    onWarehouseChange(id) {
      const found = this.warehouses.find(w => w.id === id)
      this.selectedWarehouseName = found
        ? (found.warehouseName || found.name)
        : ''
      this.loadCameras()
    },

    async loadCameras() {
      if (!this.selectedWarehouseId) return
      this.loading = true
      try {
        const res = await grainOutletDeviceApi.getWarehouseCameras(this.selectedWarehouseId)
        this.cameras = res.data || []
      } catch (e) {
        this.cameras = []
        this.$message.error(e.message || '加载监控画面失败')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.grain-monitoring-page {
  min-height: calc(100vh - 100px);
  padding: 20px;
}

.page-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.page-title {
  margin: 0 0 8px;
  font-size: 22px;
  color: #1f2937;
}

.page-desc {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
}

.warehouse-select {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.select-label {
  font-size: 14px;
  color: #4b5563;
  white-space: nowrap;
}

.empty-panel {
  background: #fff;
  border-radius: 8px;
  padding: 80px 20px;
  text-align: center;
  color: #9ca3af;
}

.empty-panel i {
  font-size: 48px;
  display: block;
  margin-bottom: 16px;
}

.empty-panel .sub {
  font-size: 13px;
  margin-top: 8px;
}

.camera-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 20px;
}

.camera-card {
  background: #1e293b;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.2);
}

.camera-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #334155;
}

.camera-title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #e2e8f0;
  font-weight: 500;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.status-dot.online {
  background: #10b981;
  box-shadow: 0 0 8px rgba(16, 185, 129, 0.6);
}

.camera-ip {
  font-size: 12px;
  color: #94a3b8;
}

.camera-feed {
  height: 220px;
  background: #0f172a;
}

.feed-placeholder {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #64748b;
}

.feed-placeholder i {
  font-size: 40px;
  opacity: 0.6;
}

.camera-card-foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  font-size: 12px;
  color: #94a3b8;
  border-top: 1px solid #334155;
}

.status-bar {
  margin-top: 20px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 8px;
  padding: 12px 16px;
  color: #047857;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
